// screen: Login (email + password)
import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui, Trans } from '@lingui/react/macro';
import { ApiError } from '@viewstream/api-client';
import { Button, Field, Input } from '@viewstream/ui';
import { useAuth } from '~/app/auth';
import { useLocale } from '~/app/locale';
import { ProductName, LocaleToggle } from '~/app/brand';
import { AccessSignIn } from '~/app/AccessSignIn';

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>) => ({ next: typeof s['next'] === 'string' ? s['next'] : undefined }),
  component: Login,
});

function Login() {
  const { t } = useLingui();
  const { login, me } = useAuth();
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<React.ReactNode>(null);
  useLocale();
  React.useEffect(() => { document.title = `${t`Sign in`} · ${__APP__ === 'admin' ? 'ViewStream Admin' : 'ViewStream Studio'}`; }, [t]);

  React.useEffect(() => {
    // Admin: only an operator session moves on; a customer session stays here and AccessSignIn exchanges the
    // Cloudflare Access identity for an operator one (admin-p6).
    if (me?.user && (__APP__ !== 'admin' || me.user.interhost_role)) {
      const slug = me.current_tenant?.slug ?? me.tenants[0]?.slug;
      if (next && next.startsWith('/') && !next.startsWith('/login')) void navigate({ to: next as never });
      else if (__APP__ === 'admin') void navigate({ to: '/admin/tenants' as never });
      else if (slug) void navigate({ to: '/t/$slug', params: { slug } });
    }
  }, [me, next, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await login(email, password);
    } catch (ex) {
      if (ex instanceof ApiError && ex.status === 429) {
        const s = ex.retryAfter ?? 60;
        setErr(t`Too many attempts. Try again in ${Math.ceil(s / 60)} min.`);
      } else if (ex instanceof ApiError && (ex.status === 401 || ex.status === 403)) {
        setErr(t`Email or password is incorrect.`); // no hint which (foundation spec)
      } else if (ex instanceof ApiError && ex.status === 422) {
        setErr(ex.problem?.errors?.map((e) => e.detail).join(' ') ?? ex.problem?.detail ?? t`Check the form.`);
      } else setErr(ex instanceof Error ? ex.message : String(ex));
    } finally { setBusy(false); }
  }

  if (__APP__ === 'admin') return <AccessSignIn />; // s2-admin-access-sso: Cloudflare Access is the sign-in

  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-8 shadow-card">
        <div className="mb-6 flex items-center justify-between">
          <ProductName large />
          <LocaleToggle />
        </div>
        <h1 className="mb-4 text-xl font-semibold">{t`Sign in`}</h1>
        <form onSubmit={onSubmit} noValidate>
          <Field id="email" label={t`Email`}>
            <Input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="ltr-isolate" dir="ltr" />
          </Field>
          <Field id="password" label={t`Password`} error={err}>
            <Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required dir="ltr" />
          </Field>
          <Button type="submit" className="w-full" loading={busy}>{t`Sign in`}</Button>
        </form>
        <p className="mt-4 text-center text-sm">
          <a className="text-primary underline-offset-4 hover:underline" href="/password/forgot"><Trans>Forgot your password?</Trans></a>
        </p>
        {/* v3 §17: self-serve sign-up and the legal pages (the register page answers "not open yet" while it is off) */}
        <p className="mt-2 text-center text-sm">
          <Trans>No account yet? <a className="text-primary underline-offset-4 hover:underline" href="/register">Create a free account</a></Trans>
        </p>
        <p className="mt-4 text-center text-xs text-fg-muted">
          <a className="underline-offset-4 hover:underline" href="https://www.viewstream.co.il/terms/" target="_blank" rel="noopener">{t`Terms of service`}</a>
          {' · '}
          <a className="underline-offset-4 hover:underline" href="https://www.viewstream.co.il/privacy/" target="_blank" rel="noopener">{t`Privacy policy`}</a>
          {' · '}
          <a className="underline-offset-4 hover:underline" href="https://www.viewstream.co.il/accessibility/" target="_blank" rel="noopener">{t`Accessibility`}</a>
        </p>
      </div>
    </main>
  );
}
